import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Driver, Severity, mockAlerts } from '../data/mockData';
import { AlertTriangle, Info, WifiOff } from 'lucide-react';
interface FleetMapProps {
  drivers: Driver[];
  selectedDriverId: string | null;
  highlightedDriverIds?: string[] | null;
  hoveredDriverIds?: string[] | null;
  onSelectDriver: (id: string) => void;
  onDeselect?: () => void;
  demoState: string;
  isRerouting?: boolean;
  hoveredRouteId?: number | null;
}
export function FleetMap({
  drivers,
  selectedDriverId,
  highlightedDriverIds,
  hoveredDriverIds,
  onSelectDriver,
  onDeselect,
  demoState,
  isRerouting,
  hoveredRouteId
}: FleetMapProps) {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const lastPanOffset = useRef({
    x: 0,
    y: 0
  });
  const containerRef = useRef<HTMLDivElement>(null);
  // Drag-to-pan state
  const [manualDragDelta, setManualDragDelta] = useState({
    x: 0,
    y: 0
  });
  const isDragging = useRef(false);
  const dragStartPos = useRef({
    x: 0,
    y: 0
  });
  const dragDistance = useRef(0);
  // Reset manual drag when programmatic pan triggers
  const prevSelectedRef = useRef(selectedDriverId);
  const prevHighlightedRef = useRef(highlightedDriverIds);
  useEffect(() => {
    if (selectedDriverId !== prevSelectedRef.current || highlightedDriverIds !== prevHighlightedRef.current) {
      setManualDragDelta({
        x: 0,
        y: 0
      });
      prevSelectedRef.current = selectedDriverId;
      prevHighlightedRef.current = highlightedDriverIds;
    }
  }, [selectedDriverId, highlightedDriverIds]);
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // Only drag on primary button, ignore if clicking on interactive elements
    if (e.button !== 0) return;
    isDragging.current = true;
    dragDistance.current = 0;
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current || !containerRef.current) return;
    const dx = e.clientX - dragStartPos.current.x;
    const dy = e.clientY - dragStartPos.current.y;
    dragDistance.current = Math.sqrt(dx * dx + dy * dy);
    const containerWidth = containerRef.current.offsetWidth;
    const containerHeight = containerRef.current.offsetHeight;
    // Convert pixel delta to percentage of container
    setManualDragDelta({
      x: dx / containerWidth * 100,
      y: dy / containerHeight * 100
    });
  }, []);
  const handlePointerUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    // Fold drag delta into lastPanOffset so it persists
    setManualDragDelta(prev => {
      lastPanOffset.current = {
        x: lastPanOffset.current.x + prev.x,
        y: lastPanOffset.current.y + prev.y
      };
      return {
        x: 0,
        y: 0
      };
    });
  }, []);
  // Filter out sleeper/inactive drivers from the map
  const visibleDrivers = drivers.filter(d => d.activityStatus !== 'sleeper');
  // Compute the visible center of the map accounting for overlay panels
  const getVisibleCenterPct = () => {
    const containerWidth = containerRef.current?.offsetWidth || 1200;
    // Left: icon rail (72px) always present; expanded panel (~392px) when multi-truck alert is active
    const leftPx = highlightedDriverIds && highlightedDriverIds.length > 0 ? 392 : 72;
    // Right: DriverDetail (380px) when a single driver is selected
    const rightPx = selectedDriverId ? 380 : 0;
    const visibleCenterX = leftPx + (containerWidth - leftPx - rightPx) / 2;
    return visibleCenterX / containerWidth * 100;
  };
  // Compute pan offset to center selected driver or group of highlighted drivers
  const panOffset = useMemo(() => {
    const visibleCenterPct = getVisibleCenterPct();
    // Case 1: Single driver selected — center on them within visible area
    if (selectedDriverId) {
      const driver = visibleDrivers.find(d => d.id === selectedDriverId);
      if (!driver) return lastPanOffset.current;
      let targetX = driver.location.x;
      let targetY = driver.location.y;
      if (isRerouting) {
        // Center between truck and next stop
        const nextStopX = driver.location.x + 7;
        const nextStopY = driver.location.y - 4 + Math.sin(0) * 2;
        targetX = (driver.location.x + nextStopX) / 2;
        targetY = (driver.location.y + nextStopY) / 2;
      }
      const offsetX = (visibleCenterPct - targetX) * 0.6;
      const offsetY = (50 - targetY) * 0.6;
      const newOffset = {
        x: offsetX,
        y: offsetY
      };
      lastPanOffset.current = newOffset;
      return newOffset;
    }
    // Case 2: Multi-truck highlight — center group bounding box within visible area
    if (highlightedDriverIds && highlightedDriverIds.length > 0) {
      const highlighted = visibleDrivers.filter(d => highlightedDriverIds.includes(d.id));
      if (highlighted.length === 0) return lastPanOffset.current;
      const xs = highlighted.map(d => d.location.x);
      const ys = highlighted.map(d => d.location.y);
      const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
      const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;
      const offsetX = (visibleCenterPct - centerX) * 0.6;
      const offsetY = (50 - centerY) * 0.6;
      const newOffset = {
        x: offsetX,
        y: offsetY
      };
      lastPanOffset.current = newOffset;
      return newOffset;
    }
    // Case 3: Nothing selected — persist last position
    return lastPanOffset.current;
  }, [selectedDriverId, highlightedDriverIds, visibleDrivers, isRerouting]);
  // Combined offset: programmatic + manual drag
  const finalOffset = {
    x: panOffset.x + manualDragDelta.x,
    y: panOffset.y + manualDragDelta.y
  };
  // Map dimensions
  const width = 800;
  const height = 600;
  // Compute status counts
  const getStatusCategory = (status: Severity): string => {
    if (status === 'critical') return 'critical';
    if (status === 'warning') return 'warning';
    if (status === 'offline') return 'offline';
    return 'ontrack';
  };
  const statusCounts = {
    critical: visibleDrivers.filter(d => d.status === 'critical').length,
    warning: visibleDrivers.filter(d => d.status === 'warning').length,
    ontrack: visibleDrivers.filter(d => d.status !== 'critical' && d.status !== 'warning' && d.status !== 'offline').length,
    offline: visibleDrivers.filter(d => d.status === 'offline').length
  };
  const getColor = (status: Severity) => {
    switch (status) {
      case 'critical':
        return '#ef4444';
      // brand-red
      case 'warning':
        return '#f59e0b';
      // brand-amber
      case 'info':
        return '#10b981';
      // treat info same as normal/green
      case 'offline':
        return '#6b7280';
      // slate-500
      default:
        return '#10b981';
      // brand-green
    }
  };
  const getDarkerColor = (status: Severity) => {
    switch (status) {
      case 'critical':
        return '#b91c1c';
      case 'warning':
        return '#d97706';
      case 'info':
        return '#047857';
      case 'offline':
        return '#475569';
      default:
        return '#047857';
    }
  };
  return <div ref={containerRef} className="w-full h-full relative bg-[#0a0f1a] overflow-hidden select-none" style={{
    cursor: isDragging.current ? 'grabbing' : 'grab'
  }} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp} onClick={e => {
    // Only deselect if this wasn't a drag
    if (dragDistance.current > 5) return;
    onDeselect?.();
    setStatusFilter(null);
  }} data-id="element-381">
      {/* Pannable map layer */}
      <motion.div className="absolute inset-0" animate={{
      x: `${finalOffset.x}%`,
      y: `${finalOffset.y}%`
    }} transition={isDragging.current ? {
      type: 'tween',
      duration: 0
    } : {
      type: 'spring',
      damping: 30,
      stiffness: 200
    }} data-id="element-382">
        {/* Background Grid / City Map Illusion */}
        <div className="absolute inset-0 opacity-20 pointer-events-none" data-id="element-383">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-384">
            <defs data-id="element-385">
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-386">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1f2937" strokeWidth="1" data-id="element-387" />
              </pattern>
              <pattern id="grid-large" width="160" height="160" patternUnits="userSpaceOnUse" data-id="element-388">
                <rect width="160" height="160" fill="url(#grid)" data-id="element-389" />
                <path d="M 160 0 L 0 0 0 160" fill="none" stroke="#374151" strokeWidth="1.5" data-id="element-390" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-large)" data-id="element-391" />

            {/* Fake highways */}
            <path d="M 0 200 Q 400 300 800 100" fill="none" stroke="#1f2937" strokeWidth="8" data-id="element-392" />
            <path d="M 200 0 Q 300 400 100 800" fill="none" stroke="#1f2937" strokeWidth="6" data-id="element-393" />
            <path d="M 0 500 Q 500 500 800 600" fill="none" stroke="#1f2937" strokeWidth="6" data-id="element-394" />
          </svg>
        </div>

        {/* Map Content Container */}
        <div className="relative w-full h-full" data-id="element-395">
          {/* Render Bridge Hazard if in bridge state */}
          {demoState === 'bridge' && <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="absolute z-20 flex flex-col items-center" style={{
          left: '48%',
          top: '48%',
          transform: 'translate(-50%, -50%)'
        }} data-id="element-396">
              <div className="w-12 h-12 bg-brand-amber/20 rounded-full flex items-center justify-center animate-pulse" data-id="element-397">
                <AlertTriangle className="w-6 h-6 text-brand-amber" data-id="element-398" />
              </div>
              <div className="bg-slate-900 border border-brand-amber/50 text-brand-amber text-[10px] font-bold px-2 py-1 rounded mt-1 shadow-lg" data-id="element-399">
                LOW CLEARANCE 12'8"
              </div>
            </motion.div>}

          {/* Render Route Line for selected driver */}
          {selectedDriverId && <svg className="absolute inset-0 w-full h-full pointer-events-none z-10" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-400">
              {visibleDrivers.map(d => {
            if (d.id !== selectedDriverId) return null;
            const x = d.location.x;
            const y = d.location.y;
            const isBridgeState = demoState === 'bridge' && d.id === 'd-david';
            const completedCount = d.route.filter(s => s.status === 'completed').length;
            const remainingStops = d.route.map((stop, index) => ({
              stop,
              index
            })).filter(({
              stop
            }) => stop.status !== 'completed');
            // Use stable slot from stop ID so positions don't shift when stops are removed
            const getStopSlot = (stopId: string): number => {
              const match = stopId.match(/^s(\d+)/);
              return match ? parseInt(match[1]) : 0;
            };
            const baseSlot = completedCount;
            const stopPositions = remainingStops.map(({
              stop
            }) => {
              const slot = getStopSlot(stop.id) - baseSlot;
              let stopX = x + slot * 7;
              let stopY = y - slot * 4 + Math.sin((slot - 1) * 0.8) * 2;
              stopX = Math.max(5, Math.min(95, stopX));
              stopY = Math.max(5, Math.min(95, stopY));
              return {
                x: stopX,
                y: stopY
              };
            });
            let pathD = `M ${x} ${y}`;
            stopPositions.forEach(pos => {
              pathD += ` L ${pos.x} ${pos.y}`;
            });
            const routeColor = isBridgeState ? '#ef4444' : getColor(d.status);
            return <g key={`route-${d.id}`} data-id="element-401">
                    {/* Actual/Planned Route */}
                    <path d={pathD} fill="none" stroke={routeColor} strokeWidth="2" strokeDasharray="none" strokeLinecap="round" className="opacity-85" vectorEffect="non-scaling-stroke" data-id="element-402" />

                    {/* Suggested Alternate Route for Bridge State */}
                    {isBridgeState && <path d={`M ${x} ${y} Q ${x + 10} ${y + 5} ${x + 15} ${y - 10}`} fill="none" stroke="#10b981" strokeWidth="2" strokeDasharray="6 4" strokeLinecap="round" className="opacity-90 animate-[dash_1s_linear_infinite]" vectorEffect="non-scaling-stroke" data-id="element-403" />}

                    {/* Suggested Alternate Route for Rerouting */}
                    {isRerouting && !isBridgeState && <>
                        <path d={`M ${x} ${y} Q ${x + 3} ${y - 18} ${stopPositions[0]?.x || x} ${stopPositions[0]?.y || y}`} fill="none" stroke="#3b82f6" strokeWidth="2" strokeDasharray="6 4" strokeLinecap="round" className="animate-[dash_1s_linear_infinite] transition-opacity duration-200" style={{
                  opacity: hoveredRouteId === 1 ? 0.9 : hoveredRouteId === null ? 0.7 : 0.25
                }} vectorEffect="non-scaling-stroke" data-id="element-404" />
                        <path d={`M ${x} ${y} Q ${x + 8} ${y + 8} ${stopPositions[0]?.x || x} ${stopPositions[0]?.y || y}`} fill="none" stroke="#3b82f6" strokeWidth="2" strokeDasharray="6 4" strokeLinecap="round" className="animate-[dash_1s_linear_infinite] transition-opacity duration-200" style={{
                  opacity: hoveredRouteId === 2 ? 0.9 : hoveredRouteId === null ? 0.7 : 0.25
                }} vectorEffect="non-scaling-stroke" data-id="element-405" />
                      </>}
                  </g>;
          })}
            </svg>}

          {/* Render Stop Nodes as HTML divs */}
          {selectedDriverId && visibleDrivers.map(d => {
          if (d.id !== selectedDriverId) return null;
          const x = d.location.x;
          const y = d.location.y;
          const completedCount = d.route.filter(s => s.status === 'completed').length;
          const remainingStops = d.route.map((stop, index) => ({
            stop,
            index
          })).filter(({
            stop
          }) => stop.status !== 'completed');
          const getStopSlot = (stopId: string): number => {
            const match = stopId.match(/^s(\d+)/);
            return match ? parseInt(match[1]) : 0;
          };
          const baseSlot = completedCount;
          const stopPositions = remainingStops.map(({
            stop
          }) => {
            const slot = getStopSlot(stop.id) - baseSlot;
            let stopX = x + slot * 7;
            let stopY = y - slot * 4 + Math.sin((slot - 1) * 0.8) * 2;
            stopX = Math.max(5, Math.min(95, stopX));
            stopY = Math.max(5, Math.min(95, stopY));
            return {
              x: stopX,
              y: stopY
            };
          });
          const routeColor = demoState === 'bridge' && d.id === 'd-david' ? '#b91c1c' : getDarkerColor(d.status);
          return remainingStops.map(({
            stop,
            index
          }, i) => {
            const pos = stopPositions[i];
            const displayNumber = completedCount + i + 1;
            return <div key={`stop-node-${stop.id}`} className="absolute z-20 pointer-events-none flex items-center justify-center rounded-full shadow-md" style={{
              left: `${pos.x}%`,
              top: `${pos.y}%`,
              transform: 'translate(-50%, -50%)',
              width: '16px',
              height: '16px',
              backgroundColor: routeColor
            }} data-id="element-406">
                    <span className="text-white font-bold text-[9px] leading-none mt-[1px]" data-id="element-407">
                      {displayNumber}
                    </span>
                  </div>;
          });
        })}

          {/* Render Trucks */}
          {visibleDrivers.map(driver => {
          const isSelected = driver.id === selectedDriverId;
          const isHovered = hoveredDriverIds?.includes(driver.id) ?? false;
          const isAlertHighlighted = highlightedDriverIds?.includes(driver.id) ?? false;
          const isHighlighted = isAlertHighlighted || isHovered;
          const hasSelectionHighlight = highlightedDriverIds && highlightedDriverIds.length > 0;
          const hasHoverHighlight = hoveredDriverIds && hoveredDriverIds.length > 0;
          const shouldDim = (hasSelectionHighlight || hasHoverHighlight) && !isHighlighted;
          const isFilteredOut = statusFilter !== null && getStatusCategory(driver.status) !== statusFilter;
          const color = getColor(driver.status);
          return <motion.div key={driver.id} className="absolute z-30 cursor-pointer group hover:!z-50" style={{
            left: `${driver.location.x}%`,
            top: `${driver.location.y}%`
          }} onClick={e => {
            e.stopPropagation();
            onSelectDriver(driver.id);
          }} whileHover={{
            scale: isFilteredOut ? 1 : 1.2
          }} animate={{
            x: '-50%',
            y: '-50%',
            scale: isSelected ? 1.3 : isHighlighted ? 1.15 : 1,
            zIndex: isSelected ? 50 : isHighlighted ? 45 : 30,
            opacity: isFilteredOut ? 0.08 : shouldDim ? 0.2 : selectedDriverId && !isSelected && !isHovered ? 0.3 : 1
          }} data-id="element-408">
                {/* Pulse effect for critical/selected/highlighted */}
                {(driver.status === 'critical' || isSelected || isHighlighted) && !isFilteredOut && <span className="absolute inset-0 rounded-full animate-ping opacity-75" style={{
              backgroundColor: color,
              transform: 'scale(2.5)'
            }} data-id="element-409" />}

                {/* Marker */}
                <div className={`w-4 h-4 rounded-full border-2 shadow-lg relative z-10 transition-colors
                    ${isSelected || isHighlighted ? 'border-white' : 'border-slate-900'}`} style={{
              backgroundColor: color
            }} data-id="element-410" />

                {/* Tooltip / Label */}
                {(() => {
              const severityOrder: Record<Severity, number> = {
                critical: 0,
                warning: 1,
                offline: 2,
                info: 3,
                normal: 4
              };
              const driverAlerts = mockAlerts.filter(a => a.driverId === driver.id || a.affectedDriverIds?.includes(driver.id)).sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
              const topAlert = driverAlerts[0] || null;
              const alertColorMap: Record<string, string> = {
                critical: 'text-brand-red',
                warning: 'text-brand-amber',
                info: 'text-brand-blue',
                offline: 'text-slate-400'
              };
              const isOffline = driver.signalBars === 0;
              return <div className={`absolute top-full mt-2 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 shadow-xl whitespace-nowrap pointer-events-none transition-opacity
                    ${driver.location.x > 75 ? 'right-0' : driver.location.x < 25 ? 'left-0' : 'left-1/2 -translate-x-1/2'}
                    ${isSelected || isHighlighted ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} data-id="element-411">
                      <div className="flex items-center gap-1.5" data-id="element-412">
                        <span className="text-[11px] font-bold font-mono text-slate-200" data-id="element-413">
                          {driver.truckId}
                        </span>
                        {isOffline ? <span className="inline-flex min-w-[98px] items-center gap-1" style={{
                    background: '#8EA0B6',
                    borderRadius: '2px',
                    padding: '1px 4px'
                  }} data-id="element-414">
                            <img src="https://cdn.magicpatterns.com/uploads/qtiWE7RgqX6uR9cCddEQgD/android_cell_4_bar_off_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24.svg" alt="No signal" className="w-3 h-3" style={{
                      filter: 'brightness(0.2)'
                    }} data-id="element-415" />
                            <span className="text-[8px] font-bold text-slate-900 whitespace-nowrap" data-id="element-416">
                              Offline
                            </span>
                            <span className="font-mono font-medium uppercase text-slate-900/60 whitespace-nowrap ml-0.5 text-[7px]" data-id="element-417">
                              {driver.lastPing}
                            </span>
                          </span> : <svg width="12" height="12" viewBox="0 0 14 14" className="shrink-0" data-id="element-418">
                            {[3, 5.5, 8, 10.5].map((h, i) => <rect key={i} x={i * 3.5} y={14 - h} width="2.5" height={h} rx="0.5" fill={i < (driver.signalBars ?? 4) ? '#94a3b8' : '#334155'} data-id="element-419" />)}
                          </svg>}
                      </div>
                      <div className="text-[10px] text-slate-400" data-id="element-420">
                        {driver.name}
                      </div>
                      {topAlert && <div className={`flex items-center gap-1 mt-1 pt-1 border-t border-slate-800 ${driver.status === 'normal' || driver.status === 'info' ? 'text-slate-500' : alertColorMap[topAlert.severity] || 'text-slate-400'}`} data-id="element-421">
                          {topAlert.severity === 'critical' || topAlert.severity === 'warning' ? <AlertTriangle className="w-3 h-3 shrink-0" data-id="element-422" /> : topAlert.severity === 'offline' ? <WifiOff className="w-3 h-3 shrink-0" data-id="element-423" /> : <Info className="w-3 h-3 shrink-0" data-id="element-424" />}
                          <span className="text-[10px] font-semibold truncate" data-id="element-425">
                            {topAlert.title}
                          </span>
                        </div>}
                    </div>;
            })()}
              </motion.div>;
        })}
        </div>
      </motion.div>

      {/* Status Filter Bar - stays fixed, not affected by pan */}
      <div className="absolute top-4 left-[88px] flex items-center gap-1.5 z-20" onClick={e => e.stopPropagation()} data-id="element-426">
        <StatusFilterPill color="bg-brand-red" label="Off Track" count={statusCounts.critical} isActive={statusFilter === 'critical'} onClick={() => setStatusFilter(statusFilter === 'critical' ? null : 'critical')} data-id="element-427" />
        <StatusFilterPill color="bg-brand-amber" label="At Risk" count={statusCounts.warning} isActive={statusFilter === 'warning'} onClick={() => setStatusFilter(statusFilter === 'warning' ? null : 'warning')} data-id="element-428" />
        <StatusFilterPill color="bg-brand-green" label="On Track" count={statusCounts.ontrack} isActive={statusFilter === 'ontrack'} onClick={() => setStatusFilter(statusFilter === 'ontrack' ? null : 'ontrack')} data-id="element-429" />
      </div>
    </div>;
}
function StatusFilterPill({
  color,
  label,
  count,
  isActive,
  onClick
}: {
  color: string;
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}) {
  return <button onClick={onClick} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all border
        ${isActive ? 'bg-slate-800 border-slate-600 text-slate-100 shadow-sm' : 'bg-slate-900/80 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700'}`} data-id="element-430">
      <div className={`w-2 h-2 rounded-full ${color}`} data-id="element-431" />
      {label}
      <span className={`text-[10px] font-bold ${isActive ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-432">
        {count}
      </span>
    </button>;
}